import { inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { MessageService } from "primeng/api";
import { Observable, take } from "rxjs";
import { ConfigService } from "./config/config.service";
import { ToasterService } from "./toaster-service";
import { MessageEnum } from "../utils/message.enum";
import { KEYCLOAK_DEFAULT_REALM } from "./keycloak/keycloak.constants";
@Injectable({
  providedIn: "root",
})
export class UtilitiesService {
  private messageService = inject(MessageService);
  private tablePageIndex: any = {};

  constructor(private configService: ConfigService, private store: Store, private readonly toaster: ToasterService) {}

  /**
   * Extract the tenant/realm subdomain from the current hostname.
   *
   * Returns null (same as visiting the root domain alone) when:
   * - domain resolver is off
   * - host equals `rootDomain` (e.g. `dev.accello.virtusa.dev`)
   * - the only extra label is `master` (e.g. `master.dev.accello.virtusa.dev`)
   *   `master` is the Keycloak default realm, not a tenant.
   */
  extractRealmFromUrl(url: string = window.location.href): string | null {
    try {
      const rootDomain = (this.configService.getConfig("rootDomain") || "").trim().toLowerCase();
      const isDomainResolver = this.configService.getConfig("isDomainResolver");
      const currentDomain = new URL(url).hostname.toLowerCase();

      if (!isDomainResolver) {
        return null;
      }

      if (!rootDomain || currentDomain === rootDomain) {
        return null;
      }

      const suffix = `.${rootDomain}`;
      if (!currentDomain.endsWith(suffix)) {
        const domainParts = currentDomain.split(".").filter(Boolean);
        return this.normalizeExtractedSubdomain(domainParts[0]);
      }

      const subdomain = currentDomain.slice(0, -suffix.length);
      // Only the first label is the tenant (e.g. wileyuat.dev.accello.virtusa.dev).
      const tenantLabel = subdomain.split(".")[0];
      return this.normalizeExtractedSubdomain(tenantLabel);
    } catch (error) {
      console.error("Failed to extract realm from URL", error);
      return null;
    }
  }

  /**
   * `master` is KEYCLOAK_DEFAULT_REALM, not a customer tenant.
   * Treat it the same as "no subdomain" so Keycloak uses accello360-master-realm.
   */
  private normalizeExtractedSubdomain(label: string | undefined): string | null {
    if (!label) {
      return null;
    }
    if (label === KEYCLOAK_DEFAULT_REALM.toLowerCase()) {
      return null;
    }
    return label;
  }

  getSubdomainUrl(subdomain: string): string {
    const protocol = window.location.protocol;
    // Always build tenant URLs relative to rootDomain (not the current hostname).
    // This prevents nested subdomains like alm-sync.master.dev.accello.virtusa.dev
    // when visiting from master.<rootDomain> — master is not a real tenant.
    const rootDomain =
      (this.configService.getConfig("rootDomain") || "").trim() ||
      window.location.hostname;
    const port = window.location.port ? `:${window.location.port}` : "";
    return `${protocol}//${subdomain}.${rootDomain}${port}`;
  }

  sortArrayByOrder(array: { order: number }[]): { order: number }[] {
    return array.sort((a, b) => a.order - b.order);
  }

  dispatchIfEmpty<T>(observable$: Observable<T[]>, action: any) {
    observable$.pipe(take(1)).subscribe((data) => {
      if (!data || data.length === 0) {
        this.store.dispatch(action);
      }
    });
  }

  updateAutomationTemplateValue = (
    template: any[],
    field: string,
    value: any
  ) => {
    const index = template.findIndex((item) => item.id === field);
    if (index !== -1) {
      template[index].value = value;
    }
  };

  /**
   * Displays a toaster notification.
   * @param message The message to display.
   * @param isSuccess Whether the notification indicates success.
   * @param showDefaultError Whether to display a default error message.
   */
  popToaster(message: string, isSuccess = true, showDefaultError?: boolean) {
    if (isSuccess) {
      this.toaster.pop({ type: 'success', text: message });
    } else {
      this.toaster.pop({ type: 'error', text: showDefaultError ? MessageEnum.SOMETHING_WENT_WRONG : message });
    }
  }

  convertFileSize(bytes: number, decimals = 0): string {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    const size = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));
    return `${size} ${sizes[i]}`;
  }

  sortByLabel(data: any[]): any[] {
    return [...(data || [])].sort((a, b) =>
      (a.label ?? '').toLowerCase().localeCompare((b.label ?? '').toLowerCase())
    );
  }

  setPageIndex(id: string, index: number) {
    this.tablePageIndex[id] = index;
  }

  clearPageIndexes() {
    this.tablePageIndex = {};
  }

  getPageIndex(id: string) {
    if(this.tablePageIndex[id]) {
      return this.tablePageIndex[id];
    } else {
      return false;
    }
  }
}
